import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

import { useAuth } from '../../contexts/AuthContext';
import { superAdminAPI } from '../../utils/api';

interface DashboardRunResult {
  companyId: string | {
    _id: string;
    companyName?: string;
    name?: string;
  };
  status: 'pending' | 'completed' | 'failed' | 'skipped';
  previousMetrics?: Record<string, number>;
  newMetrics?: Record<string, number>;
  diff?: Record<string, { before: number; after: number; delta: number }>;
  message?: string;
  resolution?: 'pending' | 'accepted' | 'rejected';
  resolvedAt?: string;
  resolvedBy?: {
    _id: string;
    username?: string;
    name?: string;
  };
  resolutionNotes?: string;
  preSnapshotId?: string;
  postSnapshotId?: string;
}

interface DashboardRun {
  _id: string;
  triggeredBy: string;
  scope: {
    allCompanies: boolean;
    companyIds?: string[];
  };
  status: 'pending' | 'running' | 'completed' | 'failed';
  results: DashboardRunResult[];
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
}

const METRIC_LABELS: Record<string, string> = {
  totalLeads: 'Total Leads',
  totalInterestedLeads: 'Interested Leads',
  totalGenericBuyingIntents: 'Generic Buying Intents',
  totalBrandedBuyingIntents: 'Branded Buying Intents',
  timesClientBrandSearched: 'Client Brand Engagement',
  competitorBrands: 'Competitor Brand Engagement',
  websiteVisitors: 'Website Visitors'
};

const DataMaintenance: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const [runs, setRuns] = useState<DashboardRun[]>([]);
  const [runsLoading, setRunsLoading] = useState<boolean>(true);
  const [runsError, setRunsError] = useState<string | null>(null);

  const [rebuildAllCompanies, setRebuildAllCompanies] = useState<boolean>(true);
  const [companyInput, setCompanyInput] = useState<string>('');
  const [rebuildLoading, setRebuildLoading] = useState<boolean>(false);
  const [rebuildError, setRebuildError] = useState<string | null>(null);
  const [rebuildSuccess, setRebuildSuccess] = useState<string | null>(null);

  const [reprocessAllCompanies, setReprocessAllCompanies] = useState<boolean>(true);
  const [reprocessCompanyInput, setReprocessCompanyInput] = useState<string>('');
  const [reprocessLoading, setReprocessLoading] = useState<boolean>(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);
  const [reprocessSuccess, setReprocessSuccess] = useState<string | null>(null);
  const [reprocessResult, setReprocessResult] = useState<any>(null);

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [diffDialogOpen, setDiffDialogOpen] = useState<boolean>(false);
  const [diffResults, setDiffResults] = useState<DashboardRunResult[]>([]);
  const [diffLoading, setDiffLoading] = useState<boolean>(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [actionLoadingMap, setActionLoadingMap] = useState<Record<string, boolean>>({});
  const [bulkActionLoading, setBulkActionLoading] = useState<boolean>(false);
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<Record<string, boolean>>({});
  const [deleteAllLoading, setDeleteAllLoading] = useState<boolean>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'all'; run?: DashboardRun | null } | null>(null);

  const isSuperAdmin = useMemo(() => user?.role === 'admin', [user?.role]);
  const deleteDialogLoading =
    deleteConfirm?.type === 'all'
      ? deleteAllLoading
      : deleteConfirm?.run
      ? Boolean(deleteLoadingMap[deleteConfirm.run._id])
      : false;

  const fetchRuns = useCallback(async () => {
    try {
      setRunsLoading(true);
      setRunsError(null);

      const response = await superAdminAPI.getDashboardRuns();
      setRuns(response.data?.data || []);
    } catch (error: any) {
      console.error('Failed to fetch dashboard rebuild runs:', error);
      setRunsError(error.response?.data?.message || error.message || 'Failed to load run history');
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchRuns();
    } else {
      setRunsLoading(false);
      setRunsError('You do not have permission to view this page.');
    }
  }, [fetchRuns, isSuperAdmin]);

  const handleRebuild = async () => {
    setRebuildError(null);
    setRebuildSuccess(null);

    const payload: { allCompanies: boolean; companyIds?: string[] } = {
      allCompanies: rebuildAllCompanies,
    };

    if (!rebuildAllCompanies) {
      const parsedCompanyIds = companyInput
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (parsedCompanyIds.length === 0) {
        setRebuildError('Please enter at least one Company ID or enable "All companies".');
        return;
      }

      payload.companyIds = parsedCompanyIds;
    }

    try {
      setRebuildLoading(true);
      const response = await superAdminAPI.rebuildDashboards(payload);
      const runId = response.data?.data?._id;
      setRebuildSuccess(runId ? `Dashboard rebuild started (Run ID: ${runId})` : 'Dashboard rebuild started.');
      setCompanyInput('');
      await fetchRuns();
    } catch (error: any) {
      console.error('Failed to trigger dashboard rebuild:', error);
      setRebuildError(error.response?.data?.message || error.message || 'Failed to trigger rebuild');
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessError(null);
    setReprocessSuccess(null);
    setReprocessResult(null);

    const payload: { allCompanies: boolean; companyIds?: string[] } = {
      allCompanies: reprocessAllCompanies,
    };

    if (!reprocessAllCompanies) {
      const parsedCompanyIds = reprocessCompanyInput
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (parsedCompanyIds.length === 0) {
        setReprocessError('Please enter at least one Company ID or enable "All companies".');
        return;
      }

      payload.companyIds = parsedCompanyIds;
    }

    try {
      setReprocessLoading(true);
      const response = await superAdminAPI.reprocessBuyingIntents(payload);
      const result = response.data?.data;
      setReprocessResult(result);
      
      const summary = result?.summary || {};
      const message = `Reprocessed ${summary.completed || 0} company(ies). ` +
        `Added ${summary.totalKeywordsAdded || 0} keywords, ` +
        `Updated ${summary.totalKeywordsUpdated || 0} keywords, ` +
        `Deactivated ${summary.totalKeywordsDeactivated || 0} old keywords.`;
      
      setReprocessSuccess(message);
      setReprocessCompanyInput('');
    } catch (error: any) {
      console.error('Failed to reprocess buying intents:', error);
      setReprocessError(error.response?.data?.message || error.message || 'Failed to reprocess buying intents');
    } finally {
      setReprocessLoading(false);
    }
  };

  const loadDiffs = useCallback(
    async (runId: string) => {
      try {
        setDiffLoading(true);
        setDiffError(null);
        setSelectedRunId(runId);
        setDiffDialogOpen(true);

        const response = await superAdminAPI.getRunDiffs(runId);
        const runData = response.data?.data;
        setDiffResults(runData?.results || []);
      } catch (error: any) {
        console.error('Failed to load diff details:', error);
        setDiffError(error.response?.data?.message || error.message || 'Failed to load diff details');
        setDiffResults([]);
      } finally {
        setDiffLoading(false);
      }
    },
    []
  );

  const closeDiffDialog = () => {
    setDiffDialogOpen(false);
    setSelectedRunId(null);
    setDiffResults([]);
    setDiffError(null);
    setActionLoadingMap({});
  };

  const handleDeleteDialogClose = () => {
    if (!deleteDialogLoading) {
      setDeleteConfirm(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) {
      return;
    }

    const currentConfirm = deleteConfirm;
    let shouldCloseDialog = true;

    try {
      if (currentConfirm.type === 'all') {
        setDeleteAllLoading(true);
        const response = await superAdminAPI.deleteAllDashboardRuns();
        const deletedCount = response.data?.data?.deletedCount ?? 0;
        setActionFeedback({
          type: 'success',
          message:
            deletedCount > 0
              ? `Deleted ${deletedCount} dashboard rebuild run${deletedCount === 1 ? '' : 's'}.`
              : 'No dashboard rebuild runs to delete.'
        });
      } else if (currentConfirm.run) {
        const runId = currentConfirm.run._id;
        setDeleteLoadingMap((prev) => ({ ...prev, [runId]: true }));
        const response = await superAdminAPI.deleteDashboardRun(runId);
        const identifier = response.data?.data?._id || runId;
        setActionFeedback({
          type: 'success',
          message: `Deleted run ${identifier}.`
        });
        if (selectedRunId === runId) {
          closeDiffDialog();
        }
      }

      await fetchRuns();
    } catch (error: any) {
      console.error('Failed to delete dashboard rebuild run:', error);
      shouldCloseDialog = false;
      setActionFeedback({
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to delete dashboard rebuild run.'
      });
    } finally {
      if (currentConfirm.type === 'all') {
        setDeleteAllLoading(false);
      } else if (currentConfirm.run) {
        const runId = currentConfirm.run._id;
        setDeleteLoadingMap((prev) => {
          const next = { ...prev };
          delete next[runId];
          return next;
        });
      }

      if (shouldCloseDialog) {
        setDeleteConfirm(null);
      }
    }
  };

  const resolveCompanies = async (action: 'accept' | 'reject', companyIds?: string[]) => {
    if (!selectedRunId) return;

    const targetResults = companyIds && companyIds.length > 0
      ? diffResults.filter((result) => {
          const ref: any = result.companyId;
          const id = typeof ref === 'object' && ref !== null ? ref._id?.toString() : String(ref);
          return companyIds.includes(id);
        })
      : diffResults.filter((result) => (result.resolution || 'pending') === 'pending');

    const idsToProcess = targetResults.map((result) => {
      const ref: any = result.companyId;
      return typeof ref === 'object' && ref !== null ? ref._id?.toString() : String(ref);
    });

    if (!idsToProcess || idsToProcess.length === 0) {
      setActionFeedback({
        type: 'error',
        message: 'No companies available to process.'
      });
      return;
    }

    const startMap: Record<string, boolean> = {};
    idsToProcess.forEach((id) => {
      startMap[id] = true;
    });
    setActionLoadingMap((prev) => ({ ...prev, ...startMap }));
    if (!companyIds) {
      setBulkActionLoading(true);
    }
    setActionFeedback(null);

    try {
      const response = await superAdminAPI.resolveRunResults(selectedRunId, action, { companyIds: idsToProcess });
      const updatedRun = response.data?.data;
      setDiffResults(updatedRun?.results || []);
      setActionFeedback({
        type: 'success',
        message:
          action === 'accept'
            ? `Accepted ${idsToProcess.length} compan${idsToProcess.length === 1 ? 'y' : 'ies'}.`
            : `Rejected ${idsToProcess.length} compan${idsToProcess.length === 1 ? 'y' : 'ies'}.`
      });
      await fetchRuns();
    } catch (error: any) {
      console.error('Failed to resolve diff:', error);
      setActionFeedback({
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to update resolution.'
      });
    } finally {
      const endMap: Record<string, boolean> = {};
      idsToProcess.forEach((id) => {
        endMap[id] = false;
      });
      setActionLoadingMap((prev) => ({ ...prev, ...endMap }));
      setBulkActionLoading(false);
    }
  };

  const runSummaryRows = useMemo(() => {
    return runs.map((run) => {
      const totalCompanies = run.results?.length || 0;
      const completed = run.results?.filter((result) => result.status === 'completed').length || 0;
      const failed = run.results?.filter((result) => result.status === 'failed').length || 0;
      const changed = run.results?.filter((result) => {
        if (!result.diff) return false;
        return Object.keys(result.diff).length > 0;
      }).length || 0;
      const pendingResolutions = run.results?.filter((result) => result.resolution === 'pending').length || 0;

      return {
        run,
        totalCompanies,
        completed,
        failed,
        changed,
        pendingResolutions
      };
    });
  }, [runs]);

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Alert severity="warning">You do not have permission to view this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Data Maintenance
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor system uploads, trigger dashboard rebuilds, and review recent maintenance runs.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
              <AutorenewIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Rebuild Dashboards
              </Typography>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={rebuildAllCompanies}
                  onChange={(event) => setRebuildAllCompanies(event.target.checked)}
                  color="primary"
                />
              }
              label="Rebuild all companies"
              sx={{ mb: 2 }}
            />

            {!rebuildAllCompanies && (
              <TextField
                label="Company IDs"
                placeholder="Enter comma-separated MongoDB IDs"
                fullWidth
                multiline
                minRows={2}
                value={companyInput}
                onChange={(event) => setCompanyInput(event.target.value)}
                helperText="Example: 605c4e... , 605c4f..."
                sx={{ mb: 2 }}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRebuild}
                disabled={rebuildLoading}
                startIcon={rebuildLoading ? <CircularProgress color="inherit" size={18} /> : <AutorenewIcon />}
              >
                {rebuildLoading ? 'Triggering...' : 'Trigger Rebuild'}
              </Button>
              <Button variant="outlined" onClick={fetchRuns} disabled={runsLoading}>
                Refresh History
              </Button>
            </Stack>

            {rebuildError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {rebuildError}
              </Alert>
            )}
            {rebuildSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {rebuildSuccess}
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
              <AutorenewIcon color="secondary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Reprocess Sheet 2 Buying Intent
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Re-process all Sheet 2 uploads with updated normalization logic. This will refresh keyword matching, fix category/label issues, and apply the latest deduplication rules.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={reprocessAllCompanies}
                  onChange={(event) => setReprocessAllCompanies(event.target.checked)}
                  color="secondary"
                />
              }
              label="Reprocess all companies"
              sx={{ mb: 2 }}
            />

            {!reprocessAllCompanies && (
              <TextField
                label="Company IDs"
                placeholder="Enter comma-separated MongoDB IDs"
                fullWidth
                multiline
                minRows={2}
                value={reprocessCompanyInput}
                onChange={(event) => setReprocessCompanyInput(event.target.value)}
                helperText="Example: 605c4e... , 605c4f..."
                sx={{ mb: 2 }}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleReprocess}
                disabled={reprocessLoading}
                startIcon={reprocessLoading ? <CircularProgress color="inherit" size={18} /> : <AutorenewIcon />}
              >
                {reprocessLoading ? 'Reprocessing...' : 'Reprocess Sheet 2'}
              </Button>
            </Stack>

            {reprocessError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {reprocessError}
              </Alert>
            )}
            {reprocessSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {reprocessSuccess}
              </Alert>
            )}

            {reprocessResult && reprocessResult.results && reprocessResult.results.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={600} display="block" mb={1}>
                  Detailed Results:
                </Typography>
                {reprocessResult.results.slice(0, 5).map((result: any, idx: number) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ mb: 0.5 }}>
                    {result.companyName}: {result.status} - {result.stats?.keywordsAdded || 0} added, {result.stats?.keywordsUpdated || 0} updated
                  </Typography>
                ))}
                {reprocessResult.results.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    ... and {reprocessResult.results.length - 5} more
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HistoryIcon color="action" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Dashboard Rebuild Runs
            </Typography>
          </Stack>
          <Button
            variant="text"
            color="error"
            startIcon={
              deleteAllLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon fontSize="small" />
            }
            onClick={() => setDeleteConfirm({ type: 'all' })}
            disabled={deleteAllLoading || runsLoading || runs.length === 0 || Boolean(deleteConfirm)}
          >
            Delete All
          </Button>
        </Stack>

        {runsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : runsError ? (
          <Alert severity="error">{runsError}</Alert>
        ) : runs.length === 0 ? (
          <Alert severity="info">No rebuild runs recorded yet.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Run ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Companies</TableCell>
                  <TableCell align="right">Changed</TableCell>
                  <TableCell align="right">Pending Actions</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="center">Review</TableCell>
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runSummaryRows.map(({ run, totalCompanies, failed, changed, pendingResolutions }) => (
                  <TableRow key={run._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {run._id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={run.status.toUpperCase()}
                        size="small"
                        color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {run.scope.allCompanies ? (
                        <Chip label="All companies" size="small" />
                      ) : (
                        <Typography variant="body2">
                          {run.scope.companyIds?.length || 0} selected
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{totalCompanies}</TableCell>
                    <TableCell align="right">{changed}</TableCell>
                    <TableCell align="right">{pendingResolutions}</TableCell>
                    <TableCell align="right">{failed}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {run.startedAt ? new Date(run.startedAt).toLocaleString() : new Date(run.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {run.completedAt ? new Date(run.completedAt).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={run.status === 'pending'}
                        onClick={() => loadDiffs(run._id)}
                      >
                        Review
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={
                          deleteLoadingMap[run._id] ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )
                        }
                        onClick={() => setDeleteConfirm({ type: 'single', run })}
                        disabled={Boolean(deleteLoadingMap[run._id]) || Boolean(deleteConfirm)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={diffDialogOpen}
        onClose={closeDiffDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Dashboard Differences
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={
                bulkActionLoading ||
                diffResults.every((result) => (result.resolution || 'pending') !== 'pending')
              }
              onClick={() => resolveCompanies('accept')}
              startIcon={bulkActionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Accept All
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              disabled={
                bulkActionLoading ||
                diffResults.every((result) => (result.resolution || 'pending') !== 'pending')
              }
              onClick={() => resolveCompanies('reject')}
            >
              Reject All
            </Button>
          </Stack>
          <IconButton onClick={closeDiffDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {diffLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : diffError ? (
            <Alert severity="error">{diffError}</Alert>
          ) : diffResults.length === 0 ? (
            <Alert severity="info">No metric differences detected for this run.</Alert>
          ) : (
            <Stack spacing={3}>
              {diffResults.map((result) => {
                const companyRef: any = result.companyId;
                const companyName =
                  typeof companyRef === 'object' && companyRef !== null
                    ? companyRef.companyName || companyRef.name || companyRef._id
                    : companyRef;
                const companyId =
                  typeof companyRef === 'object' && companyRef !== null ? companyRef._id?.toString() : String(companyRef);

                const metricKeys = Object.keys(result.diff || {});

                const resolution = result.resolution || 'pending';
                const resolutionChipColor =
                  resolution === 'accepted' ? 'success' : resolution === 'rejected' ? 'default' : 'warning';

                return (
                  <Paper
                    key={companyId}
                    elevation={0}
                    sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {companyName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Company ID: {companyId}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                        <Chip
                          label={resolution.toUpperCase()}
                          color={resolutionChipColor as any}
                          size="small"
                        />
                        <Tooltip title="Accept the new dashboard metrics for this company">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              disabled={resolution !== 'pending' || actionLoadingMap[companyId]}
                              onClick={() => resolveCompanies('accept', [companyId])}
                              startIcon={
                                actionLoadingMap[companyId] ? <CircularProgress size={14} color="inherit" /> : undefined
                              }
                            >
                              Accept
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject the new dashboard metrics for this company">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              disabled={resolution !== 'pending' || actionLoadingMap[companyId]}
                              onClick={() => resolveCompanies('reject', [companyId])}
                            >
                              Reject
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">Before</TableCell>
                            <TableCell align="right">After</TableCell>
                            <TableCell align="right">Δ</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metricKeys.map((key) => {
                            const diff = result.diff?.[key] || { before: 0, after: 0, delta: 0 };
                            const label = METRIC_LABELS[key] || key;
                            return (
                              <TableRow key={key}>
                                <TableCell>{label}</TableCell>
                                <TableCell align="right">{diff.before ?? 0}</TableCell>
                                <TableCell align="right">{diff.after ?? 0}</TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color:
                                      diff.delta > 0
                                        ? theme.palette.success.main
                                        : diff.delta < 0
                                        ? theme.palette.error.main
                                        : theme.palette.text.secondary,
                                    fontWeight: 600
                                  }}
                                >
                                  {diff.delta > 0 ? '+' : ''}
                                  {diff.delta ?? 0}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {result.resolutionNotes && result.resolutionNotes.trim() !== '' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Notes: {result.resolutionNotes}
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDiffDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteConfirm)} onClose={handleDeleteDialogClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {deleteConfirm?.type === 'all'
              ? 'This will permanently delete all dashboard rebuild runs. This action cannot be undone. Are you sure you want to continue?'
              : `This will permanently delete the selected dashboard rebuild run${
                  deleteConfirm?.run?._id ? ` (${deleteConfirm.run._id})` : ''
                }. This action cannot be undone. Proceed?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            disabled={deleteDialogLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(actionFeedback)}
        autoHideDuration={4000}
        onClose={() => setActionFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {actionFeedback ? (
          <Alert
            severity={actionFeedback.type}
            onClose={() => setActionFeedback(null)}
            sx={{ width: '100%' }}
          >
            {actionFeedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default DataMaintenance;
